import React, { FC, useState, useMemo, useCallback } from 'react';
import {
  Box,
  Checkbox,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  ListItemText,
  MenuItem,
  Typography,
  SelectChangeEvent,
  Stack,
  Select,
  colors,
  OutlinedInput,
  TextField,
} from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import CloseIcon from '@mui/icons-material/Close';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { Button } from '../../../components/core/Button/Button';
import { WebhookEndpoint, WebhookEventName } from '../types/types';
import { useWebhooksService } from '../../../hooks';
import Switch from '../../../common/Switch';
import { hasForbiddenCharForHeader, isForbiddenHeader } from '../../../util/isForbiddenHeader';
import { DialogCloseButton } from '../../../components/core/DialogCloseButton';
import { toast } from '../../../util/toast';

type WebhookModalProps = {
  /**
   * Whether the modal is open
   */
  isOpen: boolean;
  /**
   * Function to call on close of the modal
   */
  onClose: () => void;
  /**
   * Optional test Id
   */
  dataTestId?: string;
  /**
   * Webhook to edit if we are in edit mode
   */
  webhookToEdit?: WebhookEndpoint;
  /**
   * ID of the merchant
   */
  merchantId: string;
};

enum HeaderHelpText {
  FORBIDDEN = 'Protected Header',
  INVALIDCHAR = 'Header Name can only contain the following characters: a-z, 0–9, hyphen ( - ), and underscore ( _ )',
}

/**
 * Validates if a string is a valid URL with https protocol
 */
const isValidUrl = (urlString: string): boolean => {
  if (!urlString) return true; // Empty is handled by required validation
  try {
    const url = new URL(urlString);
    return url.protocol === 'https:';
  } catch {
    return false;
  }
};

const isValidEmail = (email: string): boolean => {
  if (!email) return true; // Empty is handled by required validation
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

const MAX_FAILURE_NOTIFICATION_EMAILS = 5;

const normalizeFailureEmailInputs = (emails?: string[]): string[] => {
  if (emails?.length) {
    return [...emails];
  }
  return [''];
};

const validateFailureEmailInputs = (
  inputs: string[],
): { emails: string[]; errors: string[]; isValid: boolean } => {
  const errors = inputs.map(() => '');
  const emails: string[] = [];
  const seen = new Set<string>();
  let isValid = true;

  inputs.forEach((raw, index) => {
    const trimmed = raw.trim();
    if (!trimmed) {
      if (index === 0) {
        errors[index] = 'Email is required';
        isValid = false;
      }
      return;
    }

    if (!isValidEmail(trimmed)) {
      errors[index] = 'Please enter a valid email address';
      isValid = false;
      return;
    }

    const lower = trimmed.toLowerCase();
    if (seen.has(lower)) {
      errors[index] = 'This email is already added';
      isValid = false;
      return;
    }

    seen.add(lower);
    emails.push(trimmed);
  });

  return { emails, errors, isValid };
};

const WebhookModal: FC<WebhookModalProps> = (props) => {
  const { isOpen, onClose, dataTestId = '', webhookToEdit, merchantId } = props;
  const { eventTypes, createWebhook, updateWebhook } = useWebhooksService({
    merchantId,
    skipFetch: true, // We don't need to fetch webhooks in the modal
  });

  const [endpointUrl, setEndpointUrl] = useState<string>(webhookToEdit?.endpoint ?? '');
  const [description, setDescription] = useState<string>(webhookToEdit?.description ?? '');
  const [includeChildMerchantEvents, setIncludeChildMerchantEvents] = useState(
    webhookToEdit ? webhookToEdit.includeChildMerchantEvents : false,
  );
  const [selectedEvents, setSelectedEvents] = useState<WebhookEventName[]>(
    webhookToEdit ? webhookToEdit.eventSubscriptions.map((e) => e.eventName) : [],
  );
  const [failureEmailInputs, setFailureEmailInputs] = useState<string[]>(() =>
    normalizeFailureEmailInputs(webhookToEdit?.failureNotificationEmails),
  );
  const [failureEmailErrors, setFailureEmailErrors] = useState<string[]>(() =>
    normalizeFailureEmailInputs(webhookToEdit?.failureNotificationEmails).map(() => ''),
  );
  const [secret, setSecret] = useState<string>('');
  const [isSecretEditing, setIsSecretEditing] = useState(false);
  const [secretDraft, setSecretDraft] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Extracting a custom header entry. Currently, there can only be one custom header
  const [incomingHeaderKey, incomingHeaderValue]: [string, string] =
    webhookToEdit?.customRequestHeaders
      ? Object.entries(webhookToEdit.customRequestHeaders)[0]
      : ['', ''];

  const [headerKey, setHeaderKey] = useState<string>(incomingHeaderKey);
  const [headerHelpText, setHeaderHelpText] = useState('');
  const [headerValueHelpText, setHeaderValueHelpText] = useState('');
  const [headerValue, setHeaderValue] = useState<string>(incomingHeaderValue);
  const [endpointUrlError, setEndpointUrlError] = useState('');

  const maxDescriptionLength = '1000';

  const actionButtonText = webhookToEdit ? 'Save' : 'Add';

  const maskedSecretValue = webhookToEdit?.maskedSecret;
  const hasExistingSecret = Boolean(maskedSecretValue);

  // TODO: Use react-hook-form
  const onChangeEndpointUrl = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEndpointUrl(value);
    if (value && !isValidUrl(value)) {
      setEndpointUrlError('Please enter a valid HTTPS URL');
    } else {
      setEndpointUrlError('');
    }
  };
  const onChangeDescription = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDescription(e.target.value);
  };
  const applyFailureEmailValidation = useCallback((inputs: string[]) => {
    const { errors } = validateFailureEmailInputs(inputs);
    setFailureEmailErrors(errors);
  }, []);
  const onChangeFailureEmail = (index: number, value: string) => {
    setFailureEmailInputs((previousInputs) => {
      const nextInputs = [...previousInputs];
      nextInputs[index] = value;
      applyFailureEmailValidation(nextInputs);
      return nextInputs;
    });
  };
  const onAddFailureEmail = () => {
    if (failureEmailInputs.length >= MAX_FAILURE_NOTIFICATION_EMAILS) {
      return;
    }

    setFailureEmailInputs((previousInputs) => [...previousInputs, '']);
    setFailureEmailErrors((previousErrors) => [...previousErrors, '']);
  };
  const onRemoveFailureEmail = (index: number) => {
    setFailureEmailInputs((previousInputs) => {
      if (previousInputs.length <= 1) {
        return previousInputs;
      }

      const nextInputs = previousInputs.filter((_, inputIndex) => inputIndex !== index);
      applyFailureEmailValidation(nextInputs);
      return nextInputs;
    });
    setFailureEmailErrors((previousErrors) =>
      previousErrors.filter((_, errorIndex) => errorIndex !== index),
    );
  };
  const onChangeSecret = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSecret(e.target.value);
  };
  const onChangeSecretDraft = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSecretDraft(e.target.value);
  };
  const onStartSecretEdit = () => {
    setIsSecretEditing(true);
    setSecretDraft('');
  };
  const onCancelSecretEdit = () => {
    setIsSecretEditing(false);
    setSecretDraft('');
  };
  const onChangeHeaderKey = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newHeaderKey = e.target.value;
    // TODO: Use react-hook-form's validation
    setHeaderKey(newHeaderKey);
    setHeaderValueHelpText('');
    if (!newHeaderKey) {
      setHeaderHelpText('');
    } else if (isForbiddenHeader(newHeaderKey)) {
      setHeaderHelpText(HeaderHelpText.FORBIDDEN);
    } else if (hasForbiddenCharForHeader(newHeaderKey)) {
      setHeaderHelpText(HeaderHelpText.INVALIDCHAR);
    } else {
      setHeaderHelpText('');
    }
  };
  const onChangeHeaderValue = (e: React.ChangeEvent<HTMLInputElement>) => {
    setHeaderValue(e.target.value);
  };
  const onCloseModal = () => {
    onClose();
    // Cleanup state
    setEndpointUrl('');
    setEndpointUrlError('');
    setDescription('');
    setSelectedEvents([]);
    setFailureEmailInputs(['']);
    setFailureEmailErrors(['']);
    setSecret('');
    setIsSecretEditing(false);
    setSecretDraft('');
    setHeaderKey('');
    setHeaderValue('');
    setHeaderHelpText('');
    setHeaderValueHelpText('');
  };
  const onSubmitWebhook = async () => {
    setIsSubmitting(true);

    const {
      emails: failureNotificationEmails,
      errors: failureEmailValidationErrors,
      isValid,
    } = validateFailureEmailInputs(failureEmailInputs);
    if (!isValid) {
      setFailureEmailErrors(failureEmailValidationErrors);
      setIsSubmitting(false);
      return;
    }

    if (!headerKey && headerValue) {
      setHeaderHelpText('Header key must be provided');
      setIsSubmitting(false);
      return;
    }

    if (headerKey && !headerValue) {
      setHeaderValueHelpText('Header value must be provided');
      setIsSubmitting(false);
      return;
    }

    const customRequestHeaders = headerKey ? { [headerKey]: headerValue } : null;
    try {
      if (webhookToEdit) {
        const { subscriptionId } = webhookToEdit;
        let secretUpdate = {};
        if (hasExistingSecret) {
          if (isSecretEditing) {
            secretUpdate = { secret: secretDraft };
          }
        } else if (secret) {
          secretUpdate = { secret };
        }

        const updateObject = {
          endpoint: endpointUrl,
          failureNotificationEmails,
          eventSubscriptions: selectedEvents,
          description,
          includeChildMerchantEvents,
          customRequestHeaders,
          ...secretUpdate,
        };
        await updateWebhook(subscriptionId, updateObject);
        toast.success('Webhook subscription has been updated');
      } else {
        await createWebhook(
          endpointUrl,
          failureNotificationEmails,
          selectedEvents,
          secret,
          description,
          includeChildMerchantEvents,
          customRequestHeaders || undefined,
        );
        toast.success('Webhook subscription has been created');
      }
      onCloseModal();
    } catch {
      toast.error(
        webhookToEdit
          ? 'An error occurred while updating the webhook subscription. Please try again later.'
          : 'An error occurred while creating the webhook subscription. Please try again later.',
      );
    } finally {
      setIsSubmitting(false);
    }
  };
  const onChangeSelect = (event: SelectChangeEvent<WebhookEventName[]>) => {
    setSelectedEvents(event.target.value as WebhookEventName[]);
  };

  const onToggleIncludeChildMerchants = () => {
    const newState = !includeChildMerchantEvents;
    setIncludeChildMerchantEvents(newState);
  };

  const eventTypesMapped = eventTypes.map((event) => ({
    text: event.eventDescription,
    value: event.eventName,
  }));

  const failureNotificationEmails = useMemo(() => {
    return validateFailureEmailInputs(failureEmailInputs).emails;
  }, [failureEmailInputs]);

  const isSubmitDisabled = useMemo(() => {
    return Boolean(
      !endpointUrl ||
      !isValidUrl(endpointUrl) ||
      !failureNotificationEmails.length ||
      !validateFailureEmailInputs(failureEmailInputs).isValid ||
      !selectedEvents.length ||
      (headerKey && isForbiddenHeader(headerKey)),
    );
  }, [
    endpointUrl,
    failureEmailInputs,
    failureNotificationEmails.length,
    selectedEvents,
    headerKey,
  ]);

  return (
    <Dialog open={isOpen} onClose={onCloseModal} fullWidth PaperProps={{ sx: { borderRadius: 4 } }}>
      <DialogTitle sx={{ fontWeight: 600 }}>
        {webhookToEdit ? 'Edit webhook' : 'Add webhook'}
      </DialogTitle>
      <DialogCloseButton onClose={onClose} />
      <DialogContent dividers data-testid={dataTestId}>
        <Stack spacing={2}>
          <TextField
            label="Endpoint URL"
            required
            value={endpointUrl}
            placeholder="https://"
            onChange={onChangeEndpointUrl}
            error={!!endpointUrlError}
            helperText={endpointUrlError}
            inputProps={{
              'data-testid': `${dataTestId}-endpoint`,
            }}
          />
          <FormControl required>
            <InputLabel id="events-label" sx={{ fontSize: 16 }}>
              Events
            </InputLabel>
            <Select
              labelId="events-label"
              id="events"
              data-testid={`${dataTestId}-events`}
              multiple
              value={selectedEvents}
              onChange={onChangeSelect}
              input={
                <OutlinedInput
                  label="Events"
                  sx={{
                    color: colors.common.black,
                    fontSize: 16,
                  }}
                />
              }
              renderValue={(selected) =>
                `${selected.length} event${selected.length === 1 ? '' : 's'} selected`
              }
              MenuProps={{
                PaperProps: {
                  sx: {
                    '& .MuiMenuItem-root': {
                      height: '44px',
                      '&:hover': {
                        backgroundColor: 'grey.100',
                      },
                      '&.Mui-selected': {
                        backgroundColor: 'transparent',
                        '&:hover': {
                          backgroundColor: 'grey.100',
                        },
                      },
                    },
                  },
                },
              }}
            >
              {eventTypesMapped.map((event) => (
                <MenuItem key={event.value} value={event.value} disableRipple>
                  <Checkbox checked={selectedEvents.includes(event.value)} />
                  <ListItemText
                    primary={event.text}
                    sx={{ '& .MuiTypography-root': { fontSize: '14px' } }}
                  />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Stack spacing={0.5}>
            <Typography variant="medium" fontWeight="semibold" sx={{ mb: 0.5 }}>
              Emails for failure notifications
            </Typography>
            <Stack spacing={1}>
              {failureEmailInputs.map((email, index) => (
                <TextField
                  key={`failure-email-${index}`}
                  label={index === 0 ? 'Email address' : `Email address ${index + 1}`}
                  required={index === 0}
                  value={email}
                  placeholder="Email address"
                  onChange={(event) => onChangeFailureEmail(index, event.target.value)}
                  error={Boolean(failureEmailErrors[index])}
                  helperText={failureEmailErrors[index]}
                  fullWidth
                  inputProps={{
                    'data-testid': `${dataTestId}-failureEmail-${index}`,
                  }}
                  InputProps={
                    failureEmailInputs.length > 1
                      ? {
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                aria-label={`Remove email ${index + 1}`}
                                onClick={() => onRemoveFailureEmail(index)}
                                data-testid={`${dataTestId}-removeFailureEmail-${index}`}
                                edge="end"
                                size="small"
                              >
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            </InputAdornment>
                          ),
                        }
                      : undefined
                  }
                />
              ))}
            </Stack>
            {failureEmailInputs.length < MAX_FAILURE_NOTIFICATION_EMAILS && (
              <Button
                text="Add email"
                theme="secondary"
                onClick={onAddFailureEmail}
                startIcon={<AddCircleOutlineIcon sx={{ fontSize: 18 }} />}
                dataTestId={`${dataTestId}-addFailureEmail`}
                sx={{
                  alignSelf: 'flex-start',
                  height: 36,
                  minHeight: 36,
                  px: 1.5,
                  py: 0.5,
                  mt: 0.25,
                }}
              />
            )}
            <Typography variant="small" color="text.secondary" sx={{ mt: 0.25 }}>
              Add up to {MAX_FAILURE_NOTIFICATION_EMAILS} email addresses.
            </Typography>
          </Stack>
          <Stack spacing={0.5} alignItems="flex-end">
            <TextField
              label="Webhook description"
              value={description}
              onChange={onChangeDescription}
              multiline
              rows={3}
              placeholder="Optional description of what the webhook endpoint will be used for."
              inputProps={{
                'data-testid': `${dataTestId}-description`,
                maxLength: maxDescriptionLength,
              }}
            />
            <Typography variant="small" color="text.secondary">
              {`${description?.length || 0}/${maxDescriptionLength}`}
            </Typography>
          </Stack>
          <Stack spacing={1}>
            <Typography variant="medium" fontWeight="semibold">
              Merchant Secret
            </Typography>
            {hasExistingSecret && !isSecretEditing ? (
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1,
                  px: 1.5,
                  py: 1,
                }}
              >
                <Typography
                  variant="small"
                  sx={{ fontFamily: 'monospace', flex: 1 }}
                  data-testid={`${dataTestId}-maskedSecret`}
                >
                  {maskedSecretValue}
                </Typography>
                <IconButton
                  size="small"
                  aria-label="Edit secret"
                  onClick={onStartSecretEdit}
                  data-testid={`${dataTestId}-editSecret`}
                >
                  <EditOutlinedIcon fontSize="small" />
                </IconButton>
              </Stack>
            ) : (
              <TextField
                label={hasExistingSecret ? 'New secret' : 'Merchant Secret'}
                value={hasExistingSecret ? secretDraft : secret}
                onChange={hasExistingSecret ? onChangeSecretDraft : onChangeSecret}
                placeholder={
                  hasExistingSecret
                    ? 'Enter a new secret or leave empty to remove'
                    : 'Optional code to verify this message came from us'
                }
                inputProps={{
                  'data-testid': `${dataTestId}-secret`,
                  type: 'text',
                }}
                helperText={
                  hasExistingSecret
                    ? 'Leave empty to remove the secret. Cancel to keep the current secret unchanged.'
                    : 'For extra security, set up a secret token to ensure and validate that requests sent to the URL are legitimate.'
                }
                InputProps={
                  hasExistingSecret
                    ? {
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              size="small"
                              aria-label="Cancel secret edit"
                              onClick={onCancelSecretEdit}
                              edge="end"
                              data-testid={`${dataTestId}-cancelSecretEdit`}
                            >
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }
                    : undefined
                }
              />
            )}
          </Stack>
          <Stack spacing={1}>
            <Typography variant="medium" fontWeight="semibold">
              Custom Header
            </Typography>
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                gap: { xs: 2, md: 1 },
              }}
            >
              <TextField
                label="Key"
                value={headerKey}
                placeholder="X-Custom-Header"
                onChange={onChangeHeaderKey}
                helperText={headerHelpText}
                error={!!headerHelpText}
                inputProps={{
                  maxLength: 40,
                  'data-testid': `${dataTestId}-headerKey`,
                }}
              />
              <TextField
                label="Value"
                value={headerValue}
                placeholder="Bearer token123"
                onChange={onChangeHeaderValue}
                helperText={headerValueHelpText}
                error={!!headerValueHelpText}
                inputProps={{
                  maxLength: 8192,
                  'data-testid': `${dataTestId}-headerValue`,
                }}
              />
            </Box>
          </Stack>
        </Stack>
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{ mt: 3 }}
          data-testid={dataTestId ? `${dataTestId}-includeChildMerchantSection` : undefined}
        >
          <Switch
            isChecked={includeChildMerchantEvents}
            onToggle={onToggleIncludeChildMerchants}
            data-testid={dataTestId ? `${dataTestId}-includeChildMerchantToggle` : undefined}
          />
          <Typography variant="small">
            Enable this subscription to receive events from all associated merchants using Skipify.
          </Typography>
        </Stack>
        <Divider sx={{ mt: 4, mb: 2 }} />
        <Stack direction="row" spacing={1}>
          <Button
            text="Cancel"
            theme="secondary"
            onClick={onCloseModal}
            isFullWidth
            dataTestId={`${dataTestId}-modalCancelButton`}
          />
          <Button
            text={actionButtonText}
            theme="primary"
            onClick={onSubmitWebhook}
            disabled={isSubmitDisabled}
            loading={isSubmitting}
            isFullWidth
            dataTestId={`${dataTestId}-modalAddButton`}
          />
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default WebhookModal;
